import { countryCodeToEmoji } from "@/lib/flags";
import { isStateRequiredForCountry } from "@kraahl/contract";

// PayPal supported countries for shipping/checkout
// Source: https://developer.paypal.com/api/rest/reference/country-codes/

export interface PayPalCountry {
  code: string; // ISO country code for PayPal
  name: string; // Full country name in English
}

// Type compatible avec l'ancien système pour la transition
export interface PayPalCountryWithIso extends PayPalCountry {
  isoCode: string; // Même que code, pour compatibilité
  flag: string;   // URL du drapeau
}

export const PAYPAL_SUPPORTED_COUNTRIES: PayPalCountry[] = [
  { code: "AL", name: "Albania" },
  { code: "DZ", name: "Algeria" },
  { code: "AD", name: "Andorra" },
  { code: "AO", name: "Angola" },
  { code: "AI", name: "Anguilla" },
  { code: "AG", name: "Antigua & Barbuda" },
  { code: "AR", name: "Argentina" },
  { code: "AM", name: "Armenia" },
  { code: "AW", name: "Aruba" },
  { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" },
  { code: "AZ", name: "Azerbaijan" },
  { code: "BS", name: "Bahamas" },
  { code: "BH", name: "Bahrain" },
  { code: "BB", name: "Barbados" },
  { code: "BY", name: "Belarus" },
  { code: "BE", name: "Belgium" },
  { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" },
  { code: "BM", name: "Bermuda" },
  { code: "BT", name: "Bhutan" },
  { code: "BO", name: "Bolivia" },
  { code: "BA", name: "Bosnia & Herzegovina" },
  { code: "BW", name: "Botswana" },
  { code: "BR", name: "Brazil" },
  { code: "VG", name: "British Virgin Islands" },
  { code: "BN", name: "Brunei" },
  { code: "BG", name: "Bulgaria" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "KH", name: "Cambodia" },
  { code: "CM", name: "Cameroon" },
  { code: "CA", name: "Canada" },
  { code: "CV", name: "Cape Verde" },
  { code: "KY", name: "Cayman Islands" },
  { code: "TD", name: "Chad" },
  { code: "CL", name: "Chile" },
  { code: "C2", name: "China" },
  { code: "CO", name: "Colombia" },
  { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo - Brazzaville" },
  { code: "CD", name: "Congo - Kinshasa" },
  { code: "CK", name: "Cook Islands" },
  { code: "CR", name: "Costa Rica" },
  { code: "CI", name: "Côte d'Ivoire" },
  { code: "HR", name: "Croatia" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czech Republic" },
  { code: "DK", name: "Denmark" },
  { code: "DJ", name: "Djibouti" },
  { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "EC", name: "Ecuador" },
  { code: "EG", name: "Egypt" },
  { code: "SV", name: "El Salvador" },
  { code: "ER", name: "Eritrea" },
  { code: "EE", name: "Estonia" },
  { code: "ET", name: "Ethiopia" },
  { code: "FK", name: "Falkland Islands" },
  { code: "FO", name: "Faroe Islands" },
  { code: "FJ", name: "Fiji" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "GF", name: "French Guiana" },
  { code: "PF", name: "French Polynesia" },
  { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" },
  { code: "GE", name: "Georgia" },
  { code: "DE", name: "Germany" },
  { code: "GI", name: "Gibraltar" },
  { code: "GR", name: "Greece" },
  { code: "GL", name: "Greenland" },
  { code: "GD", name: "Grenada" },
  { code: "GP", name: "Guadeloupe" },
  { code: "GT", name: "Guatemala" },
  { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "GY", name: "Guyana" },
  { code: "HN", name: "Honduras" },
  { code: "HK", name: "Hong Kong SAR China" },
  { code: "HU", name: "Hungary" },
  { code: "IS", name: "Iceland" },
  { code: "IN", name: "India" },
  { code: "ID", name: "Indonesia" },
  { code: "IE", name: "Ireland" },
  { code: "IL", name: "Israel" },
  { code: "IT", name: "Italy" },
  { code: "JM", name: "Jamaica" },
  { code: "JP", name: "Japan" },
  { code: "JO", name: "Jordan" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KE", name: "Kenya" },
  { code: "KI", name: "Kiribati" },
  { code: "KW", name: "Kuwait" },
  { code: "KG", name: "Kyrgyzstan" },
  { code: "LA", name: "Laos" },
  { code: "LV", name: "Latvia" },
  { code: "LS", name: "Lesotho" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "MK", name: "Macedonia" },
  { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" },
  { code: "MY", name: "Malaysia" },
  { code: "MV", name: "Maldives" },
  { code: "ML", name: "Mali" },
  { code: "MT", name: "Malta" },
  { code: "MH", name: "Marshall Islands" },
  { code: "MQ", name: "Martinique" },
  { code: "MR", name: "Mauritania" },
  { code: "MU", name: "Mauritius" },
  { code: "YT", name: "Mayotte" },
  { code: "MX", name: "Mexico" },
  { code: "FM", name: "Micronesia" },
  { code: "MD", name: "Moldova" },
  { code: "MC", name: "Monaco" },
  { code: "MN", name: "Mongolia" },
  { code: "ME", name: "Montenegro" },
  { code: "MS", name: "Montserrat" },
  { code: "MA", name: "Morocco" },
  { code: "MZ", name: "Mozambique" },
  { code: "NA", name: "Namibia" },
  { code: "NR", name: "Nauru" },
  { code: "NP", name: "Nepal" },
  { code: "NL", name: "Netherlands" },
  { code: "NC", name: "New Caledonia" },
  { code: "NZ", name: "New Zealand" },
  { code: "NI", name: "Nicaragua" },
  { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" },
  { code: "NU", name: "Niue" },
  { code: "NF", name: "Norfolk Island" },
  { code: "NO", name: "Norway" },
  { code: "OM", name: "Oman" },
  { code: "PW", name: "Palau" },
  { code: "PA", name: "Panama" },
  { code: "PG", name: "Papua New Guinea" },
  { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" },
  { code: "PH", name: "Philippines" },
  { code: "PN", name: "Pitcairn Islands" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "QA", name: "Qatar" },
  { code: "RE", name: "Réunion" },
  { code: "RO", name: "Romania" },
  { code: "RU", name: "Russia" },
  { code: "RW", name: "Rwanda" },
  { code: "WS", name: "Samoa" },
  { code: "SM", name: "San Marino" },
  { code: "ST", name: "São Tomé & Príncipe" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "SN", name: "Senegal" },
  { code: "RS", name: "Serbia" },
  { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SG", name: "Singapore" },
  { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" },
  { code: "SB", name: "Solomon Islands" },
  { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" },
  { code: "KR", name: "South Korea" },
  { code: "ES", name: "Spain" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SH", name: "St. Helena" },
  { code: "KN", name: "St. Kitts & Nevis" },
  { code: "LC", name: "St. Lucia" },
  { code: "PM", name: "St. Pierre & Miquelon" },
  { code: "VC", name: "St. Vincent & Grenadines" },
  { code: "SR", name: "Suriname" },
  { code: "SJ", name: "Svalbard & Jan Mayen" },
  { code: "SZ", name: "Swaziland" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "TW", name: "Taiwan" },
  { code: "TJ", name: "Tajikistan" },
  { code: "TZ", name: "Tanzania" },
  { code: "TH", name: "Thailand" },
  { code: "TG", name: "Togo" },
  { code: "TO", name: "Tonga" },
  { code: "TT", name: "Trinidad & Tobago" },
  { code: "TN", name: "Tunisia" },
  { code: "TM", name: "Turkmenistan" },
  { code: "TC", name: "Turks & Caicos Islands" },
  { code: "TV", name: "Tuvalu" },
  { code: "UG", name: "Uganda" },
  { code: "UA", name: "Ukraine" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "UY", name: "Uruguay" },
  { code: "VU", name: "Vanuatu" },
  { code: "VA", name: "Vatican" },
  { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Vietnam" },
  { code: "WF", name: "Wallis & Futuna" },
  { code: "YE", name: "Yemen" },
  { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" },
];

// Helper functions
export function getCountryNameByCode(countryCode: string): string {
  const country = PAYPAL_SUPPORTED_COUNTRIES.find(c => c.code === countryCode);
  return country?.name || countryCode;
}

export function getCountryCodeByName(countryName: string): string {
  const country = PAYPAL_SUPPORTED_COUNTRIES.find(c => c.name === countryName);
  return country?.code || countryName;
}

export function isPayPalSupportedCountry(countryCode: string): boolean {
  return PAYPAL_SUPPORTED_COUNTRIES.some(c => c.code === countryCode);
}

// États américains (requis pour US)
export const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];

// Provinces canadiennes (requis pour CA)
export const CA_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "YT", name: "Yukon" },
];

// Helper pour obtenir les états/provinces selon le pays
export function getStatesForCountry(countryCode: string) {
  switch (countryCode) {
    case "US":
      return US_STATES;
    case "CA":
      return CA_PROVINCES;
    default:
      return [];
  }
}

// Helper pour obtenir le nom complet d'un pays à partir de son code
export function getCountryName(countryCode: string): string {
  const country = PAYPAL_SUPPORTED_COUNTRIES.find(c => c.code === countryCode);
  return country?.name || countryCode;
}

// Helper pour obtenir le nom complet d'un état/province à partir de son code
export function getStateName(stateCode: string, countryCode: string): string {
  const states = getStatesForCountry(countryCode);
  const state = states.find(s => s.code === stateCode);
  return state?.name || stateCode;
}

// Helper pour obtenir le code d'état à partir de son nom (inverse de getStateName)
export function getStateCodeByName(stateName: string, countryCode: string): string {
  const states = getStatesForCountry(countryCode);
  const state = states.find(s => s.name.toLowerCase() === stateName.toLowerCase());
  return state?.code || stateName; // Retourner le nom original si pas trouvé
}

// Alias pour getStateName - maintient la compatibilité avec le code existant
export function getStateOrRegionName(stateValue: string, countryCode: string): string {
  // Pour US et CA, utiliser la fonction getStateName pour convertir les codes
  if (isStateRequiredForCountry(countryCode)) {
    return getStateName(stateValue, countryCode);
  }
  // Pour les autres pays, retourner directement la valeur saisie (texte libre)
  return stateValue;
}

// Helper pour convertir state/région en format PayPal admin_area_1
export function formatStateForPayPal(stateValue: string, countryCode: string): string {
  // Pour US et CA, convertir le nom en code si nécessaire
  if (isStateRequiredForCountry(countryCode)) {
    // Si c'est déjà un code (2-3 caractères majuscules), le retourner tel quel
    if (/^[A-Z]{2,3}$/.test(stateValue)) {
      return stateValue;
    }
    // Sinon, essayer de convertir le nom en code
    return getStateCodeByName(stateValue, countryCode);
  }
  // Pour les autres pays, retourner directement la valeur saisie
  return stateValue;
}

export function transformPayPalCountriesToDropdownOptions() {
  return PAYPAL_SUPPORTED_COUNTRIES.map(country => {
    // Mapping spécial pour les drapeaux : PayPal C2 = Chine
    const flagCode = country.code === "C2" ? "CN" : country.code;
    
    return {
      code: country.code, // Garde le code PayPal original (C2 pour la Chine)
      name: country.name,
      flag: countryCodeToEmoji(flagCode), // Emoji du drapeau
      isoCode: country.code, // Garde le code PayPal original
    };
  });
}

// Liste compatible avec l'ancien format (pour shipping-method-dialog)
export function getPayPalCountriesWithIsoFormat(): PayPalCountryWithIso[] {
  return PAYPAL_SUPPORTED_COUNTRIES.map(country => {
    // Mapping spécial pour les drapeaux : PayPal C2 = Chine, mais drapeau = cn
    const flagCode = country.code === "C2" ? "cn" : country.code.toLowerCase();
    
    return {
      code: country.code, // Garde le code PayPal original (C2 pour la Chine)
      name: country.name,
      isoCode: country.code, // Dans PayPal, code et isoCode sont identiques
      flag: `https://flagcdn.com/24x18/${flagCode}.png`, // Utilise le bon code pour le drapeau
    };
  });
}

// Transforme les états/provinces en format compatible avec SearchableDropdown
export function transformStatesToDropdownOptions(states: { code: string; name: string }[]) {
  return states.map(state => ({
    code: state.code,
    name: state.name,
    flag: "", // Pas de drapeau pour les états
    isoCode: state.code,
  }));
}

// Mapping des codes ISO standards vers les codes PayPal (cas spéciaux uniquement)
const ISO_TO_PAYPAL_MAPPING: Record<string, string> = {
  "CN": "C2", // Chine utilise C2 au lieu de CN dans PayPal
};

// Mapping inverse PayPal vers ISO
const PAYPAL_TO_ISO_MAPPING: Record<string, string> = {
  "C2": "CN", // PayPal C2 = Chine ISO CN
};

// Convertit un code ISO standard vers le code PayPal équivalent
export function isoToPayPalCode(isoCode: string): string {
  return ISO_TO_PAYPAL_MAPPING[isoCode] || isoCode;
}

// Convertit un code PayPal vers le code ISO standard équivalent
export function payPalToIsoCode(paypalCode: string): string {
  return PAYPAL_TO_ISO_MAPPING[paypalCode] || paypalCode;
}

// Vérifie si un pays (code ISO) est supporté par PayPal
export function isCountrySupportedByPayPal(isoCode: string): boolean {
  const paypalCode = isoToPayPalCode(isoCode);
  return PAYPAL_SUPPORTED_COUNTRIES.some(country => country.code === paypalCode);
}

// Vérifie si un pays est banni pour une méthode de shipping donnée
// Prend en compte les correspondances ISO <-> PayPal
export function isCountryBannedForShipping(countryCode: string, bannedCountries: string[]): boolean {
  // Le countryCode peut être soit ISO soit PayPal
  // bannedCountries contient normalement des codes ISO (de l'ancien système)
  
  // 1. Vérifier directement
  if (bannedCountries.includes(countryCode)) {
    return true;
  }
  
  // 2. Si countryCode est PayPal, convertir en ISO et vérifier
  const isoCode = payPalToIsoCode(countryCode);
  if (isoCode !== countryCode && bannedCountries.includes(isoCode)) {
    return true;
  }
  
  // 3. Si countryCode est ISO, convertir en PayPal et vérifier
  const paypalCode = isoToPayPalCode(countryCode);
  if (paypalCode !== countryCode && bannedCountries.includes(paypalCode)) {
    return true;
  }
  
  return false;
}

// Fonction pour diagnostiquer les problèmes de correspondance entre ISO et PayPal
export function validateCountryCodeMappings() {
  const issues: string[] = [];
  
  // Vérifier que tous les codes PayPal correspondent à des codes ISO valides
  PAYPAL_SUPPORTED_COUNTRIES.forEach(country => {
    if (country.code === "C2") {
      // Exception connue pour la Chine
      return;
    }
    
    // Pour les autres, le code PayPal devrait correspondre à un code ISO
    try {
      const countryName = new Intl.DisplayNames(["en"], { type: "region" }).of(country.code);
      if (!countryName) {
        issues.push(`Code PayPal "${country.code}" (${country.name}) ne correspond pas à un code ISO valide`);
      }
    } catch (e) {
      issues.push(`Code PayPal "${country.code}" (${country.name}) invalide en tant que code ISO`);
    }
  });
  
  return {
    hasIssues: issues.length > 0,
    issues,
    totalCountries: PAYPAL_SUPPORTED_COUNTRIES.length,
    knownExceptions: ["C2"], // Chine
  };
}

// Fonction utilitaire pour migrer les anciens banned_countries vers le nouveau système
export function migrateBannedCountriesToPayPal(oldBannedCountries: string[]): string[] {
  return oldBannedCountries.map(code => isoToPayPalCode(code));
}
